import React, { useState, useEffect } from 'react';
import type { StorageUsage } from '../../types';
import { ApiClient } from '../../api/client';
import {
  HardDrive,
  Save,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ShieldAlert,
  FileCheck,
  Building2,
  ArrowUpRight,
  ArrowDownLeft,
} from 'lucide-react';

function fmtBytes(n?: number): string {
  if (!n || n === 0) return '0 B';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
  return (n / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

export const UploadPolicyAndStatsView: React.FC = () => {
  const [maxSize, setMaxSize] = useState('');
  const [allowedExt, setAllowedExt] = useState('');
  const [deniedExtra, setDeniedExtra] = useState('');
  const [defaultDenied, setDefaultDenied] = useState('');

  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [loadingUsage, setLoadingUsage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const loadPolicy = async () => {
    setLoadingPolicy(true);
    try {
      const res = await ApiClient.getUploadPolicy();
      if (res.ok && res.data) {
        setMaxSize(res.data.upload_max_size_mb || '');
        setAllowedExt(res.data.upload_allowed_ext || '');
        setDeniedExtra(res.data.upload_denied_ext_extra || '');
        setDefaultDenied(
          res.data.upload_denied_ext_default ||
          'exe,com,bat,cmd,msi,msp,scr,pif,vbs,vbe,js,jse,wsf,wsh,ps1,ps1xml,psc1,cpl,dll,sys,drv,gadget,application,lnk,sh,bash,run,bin,out,elf,deb,rpm,appimage,app,command,workflow,dmg,pkg,jar,apk'
        );
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoadingPolicy(false);
    }
  };

  const loadUsage = async () => {
    setLoadingUsage(true);
    try {
      const res = await ApiClient.getUsage();
      if (res.ok && res.data) {
        setUsage(res.data);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    loadPolicy();
    loadUsage();
  }, []);

  const handleSavePolicy = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const res = await ApiClient.saveUploadPolicy({
        upload_max_size_mb: maxSize.trim(),
        upload_allowed_ext: allowedExt.trim().toLowerCase(),
        upload_denied_ext_extra: deniedExtra.trim().toLowerCase(),
      });
      if (res.ok) {
        setFeedback({ type: 'ok', text: '✅ Fájlfeltöltési szabályok sikeresen elmentve!' });
        await loadPolicy();
      } else {
        setFeedback({ type: 'err', text: res.error || 'Hiba a szabályok mentésekor' });
      }
    } catch (e: any) {
      setFeedback({ type: 'err', text: 'Hálózati hiba: ' + e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrphan = async (attId: number) => {
    if (!confirm('Véglegesen törli ezt a fájlt? Ez nem vonható vissza. Ellenőrizte a megőrzési kötelezettséget?')) {
      return;
    }
    try {
      const res = await ApiClient.deleteAttachment(attId);
      if (res.ok) {
        await loadUsage();
      } else {
        alert(res.error || 'Hiba a törléskor');
      }
    } catch (e: any) {
      alert(e.message);
    }
  };

  const storageData = usage?.storage || { bytes: 0, files: 0, free_tier_bytes: 10 * 1024 * 1024 * 1024, percent: 0 };
  const ops = usage?.ops_this_month || { r2_write: 0, r2_read: 0, yyyymm: '', free_tier_writes: 1000000, free_tier_reads: 10000000 };
  const perTenant = usage?.per_tenant_storage || [];
  const orphans = usage?.orphans || [];

  return (
    <div className="space-y-6">
      {/* Címsor */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
            <span>Rendszerkezelés & Erőforrások</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2 mt-0.5">
            <HardDrive className="w-6 h-6 text-blue-600" />
            <span>Fájlfeltöltési Szabályok & Tárhely Statisztika</span>
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Feltöltési mérethatárok, kiterjesztési házirend, valamint Cloudflare R2 tárhely- és műveletstatisztika.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center gap-2.5 animate-in fade-in ${
            feedback.type === 'ok'
              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
              : 'bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {feedback.type === 'ok' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
          )}
          <span>{feedback.text}</span>
        </div>
      )}

      {/* 1. KÁRTYA: FÁJLFELTÖLTÉSI SZABÁLYOK (3. KÉP ALAPJÁN) */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/30">
          <span className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-blue-600" />
            <span>Fájlfeltöltési szabályok (mellékletek)</span>
          </span>
          <span className="text-[11px] text-slate-400">Biztonsági korlátok és kvóták</span>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Max fájlméret */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Max. fájlméret (MB)
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={maxSize}
                onChange={(e) => setMaxSize(e.target.value)}
                placeholder="pl. 10 – üresen hagyva nincs korlát"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>

            {/* Engedélyezett kiterjesztések */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Engedélyezett kiterjesztések
              </label>
              <input
                type="text"
                value={allowedExt}
                onChange={(e) => setAllowedExt(e.target.value)}
                placeholder="pl. pdf,docx,xlsx,jpg,png – üresen hagyva bármi típus"
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
            </div>
          </div>

          {/* További tiltott kiterjesztések */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
              További tiltott kiterjesztések (opcionális)
            </label>
            <input
              type="text"
              value={deniedExtra}
              onChange={(e) => setDeniedExtra(e.target.value)}
              placeholder="pl. zip,iso – ezekkel bővül az alapértelmezett tiltólista"
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
          </div>

          <div>
            <button
              type="button"
              onClick={handleSavePolicy}
              disabled={saving || loadingPolicy}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-xs transition cursor-pointer"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>Szabályok mentése</span>
            </button>
          </div>

          <div className="pt-3 border-t border-slate-100 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 space-y-2 leading-relaxed">
            <p>
              A korlátok minden felhasználóra vonatkoznak, amikor fájlt csatolnak egy ponthoz (egyedi vagy tömeges feltöltés). A kiterjesztéseket vessző válassza el, pont nélkül.
            </p>
            <p>
              Az alábbi futtatható / veszélyes kiterjesztések <strong className="text-red-600 dark:text-red-400">mindig</strong> tiltottak, ez nem kapcsolható ki – az adminisztrátor csak bővítheti ezt a listát:
            </p>
            <div className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-950 font-mono text-[10px] text-slate-600 dark:text-slate-400 break-all leading-normal border border-slate-200 dark:border-slate-800">
              {defaultDenied}
            </div>
          </div>
        </div>
      </div>

      {/* 2. KÁRTYA: HASZNÁLAT & TÁRHELY STATISZTIKA */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/30">
          <span className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-emerald-600" />
            <span>Tárhely- és műveletszám-használat (R2)</span>
          </span>
          <button
            type="button"
            onClick={loadUsage}
            disabled={loadingUsage}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingUsage ? 'animate-spin' : ''}`} />
            <span>Frissítés</span>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-[11px] text-slate-400">
            A rendszerben nyomon követett statisztika a Cloudflare R2 ingyenes szintjéhez képest – valós idejű telemetria a csatolt állományokról és műveletekről.
          </p>

          {/* Mérők */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Tárhely */}
            <div className="p-4 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                <span className="flex items-center gap-1.5">
                  <HardDrive className="w-4 h-4 text-blue-500" /> Tárhely (R2)
                </span>
                <span className="text-[11px] font-mono text-slate-500">{storageData.files} fájl</span>
              </div>
              <div className="text-lg font-bold text-slate-900 dark:text-white font-mono">
                {fmtBytes(storageData.bytes)} <span className="text-xs text-slate-400 font-normal">/ {fmtBytes(storageData.free_tier_bytes)}</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full"
                  style={{ width: `${Math.min(100, Math.max(1, (storageData.bytes / (storageData.free_tier_bytes || 1)) * 100))}%` }}
                />
              </div>
            </div>

            {/* Írási műveletek */}
            <div className="p-4 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                <span className="flex items-center gap-1.5">
                  <ArrowUpRight className="w-4 h-4 text-purple-500" /> Havi írási műveletek
                </span>
                <span className="text-[10px] font-mono text-slate-400">{ops.yyyymm || 'Havi'}</span>
              </div>
              <div className="text-lg font-bold text-slate-900 dark:text-white font-mono">
                {(ops.r2_write || 0).toLocaleString('hu-HU')} <span className="text-xs text-slate-400 font-normal">/ {(ops.free_tier_writes || 1000000).toLocaleString('hu-HU')}</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-600 rounded-full"
                  style={{ width: `${Math.min(100, Math.max(1, ((ops.r2_write || 0) / (ops.free_tier_writes || 1000000)) * 100))}%` }}
                />
              </div>
            </div>

            {/* Olvasási műveletek */}
            <div className="p-4 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl space-y-2">
              <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                <span className="flex items-center gap-1.5">
                  <ArrowDownLeft className="w-4 h-4 text-emerald-500" /> Havi olvasási műveletek
                </span>
                <span className="text-[10px] font-mono text-slate-400">{ops.yyyymm || 'Havi'}</span>
              </div>
              <div className="text-lg font-bold text-slate-900 dark:text-white font-mono">
                {(ops.r2_read || 0).toLocaleString('hu-HU')} <span className="text-xs text-slate-400 font-normal">/ {(ops.free_tier_reads || 10000000).toLocaleString('hu-HU')}</span>
              </div>
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-600 rounded-full"
                  style={{ width: `${Math.min(100, Math.max(1, ((ops.r2_read || 0) / (ops.free_tier_reads || 10000000)) * 100))}%` }}
                />
              </div>
            </div>
          </div>

          {/* Szervezetenkénti felbontás */}
          {perTenant.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-blue-500" />
                <span>Tárhely szervezetenként</span>
              </h4>
              <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 dark:bg-slate-950/50 text-slate-500 uppercase font-bold text-[10px] border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="py-2.5 px-4">Szervezet</th>
                      <th className="py-2.5 px-4 text-right">Csatolt fájlok</th>
                      <th className="py-2.5 px-4 text-right">Felhasznált tárhely</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
                    {perTenant.map((t) => (
                      <tr key={t.tenant_id} className="hover:bg-slate-50/50 dark:hover:bg-slate-950/20">
                        <td className="py-2.5 px-4 font-sans font-medium text-slate-800 dark:text-slate-200">
                          {t.name}
                        </td>
                        <td className="py-2.5 px-4 text-right text-slate-500">{t.files} db</td>
                        <td className="py-2.5 px-4 text-right text-slate-900 dark:text-white font-bold">
                          {fmtBytes(t.bytes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Árva mellékletek */}
          <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div>
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4 text-amber-500" />
                <span>Árva mellékletek (törölt ponthoz tartozó, de megmaradt fájlok)</span>
              </h4>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Ezek a fájlok olyan pontokhoz tartoztak, amelyeket időközben töröltek. Törlés előtt ellenőrizze a megőrzési kötelezettséget.
              </p>
            </div>

            {orphans.length === 0 ? (
              <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/60 rounded-xl text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Nincs árva melléklet az adattárban. Minden fájl aktív közzétételi ponthoz kapcsolódik. ✅</span>
              </div>
            ) : (
              <div className="space-y-2">
                {orphans.map((o) => (
                  <div
                    key={o.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-slate-50 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl text-xs"
                  >
                    <div className="truncate flex-1">
                      <span className="font-bold text-slate-800 dark:text-slate-200">{o.filename}</span>
                      <span className="text-slate-400 text-[11px]"> — {o.page_title || 'Ismeretlen oldal'} {o.tenant_name ? ` · ${o.tenant_name}` : ''}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 font-mono text-slate-500 text-[11px]">
                      <span>{fmtBytes(o.size)}</span>
                      <span>{o.uploaded_at ? new Date(o.uploaded_at).toLocaleDateString('hu-HU') : ''}</span>
                      <button
                        type="button"
                        onClick={() => handleDeleteOrphan(o.id)}
                        className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1 cursor-pointer transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Végleges törlés</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
